
import { Vue, Options, prop } from "vue-class-component";
import { DatePicker } from "v-calendar";
import OverlayPanel from "primevue/overlaypanel";
import moment from "moment";

function todayStart(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function toYYYYMMDD(d: Date | null): string {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseYYYYMMDD(s: string | null | undefined): Date | null {
  if (!s) return null;
  return moment(s).toDate();
}

class Props {
  startDate = prop<string>({ default: "", type: String });
  endDate = prop<string>({ default: "", type: String });
  showCalendar = prop<boolean>({ default: false, type: Boolean });
  maxSelectableDays = prop<number>({ default: 4, type: Number });
}

const OVERLAY_ID = "search-flight-date-picker-overlay";
const OVERLAY_SEL = `#${OVERLAY_ID}, .search-flight-date-picker-overlay`;

function nodeContainsOverlay(node: Node): boolean {
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  const el = node as Element;
  return (
    el.id === OVERLAY_ID ||
    el.classList?.contains("search-flight-date-picker-overlay") === true ||
    !!el.querySelector(OVERLAY_SEL)
  );
}

const CARGO_SEARCH_FLIGHT_MAX_SELECTABLE_DAYS = 15;

@Options({
  emits: ["update:startDate", "update:endDate", "change"],
  components: {
    DatePicker,
    OverlayPanel,
  },
})
class SearchFlightDatePicker extends Vue.with(Props) {
  opCalendar: any = null;
  picker: any = null;
  isCursorInsideCalendar = false;
  displayBase: string | null = null;
  isOverlayVisible = false;
  private overlayObserver: MutationObserver | null = null;

  setOverlayVisible(visible: boolean): void {
    this.isOverlayVisible = visible;
  }

  get value(): string {
    return this.startDate ?? "";
  }

  set value(v: string) {
    this.$emit("update:startDate", v);
    this.$emit("update:endDate", "");
    this.$emit("change", v, "");
  }

  get endValue(): string {
    return this.endDate ?? "";
  }

  set endValue(v: string) {
    this.$emit("update:endDate", v);
  }

  get displayDates(): { date: Date; key: string }[] {
    const today = todayStart();
    const base: Date = this.displayBase
      ? parseYYYYMMDD(this.displayBase) ?? today
      : today;
    const length = this.maxSelectableDays;
    const arr = Array.from({ length }, (_, index) => index);
    return arr.map((i) => {
      const d = addDays(base, i);
      return { date: d, key: toYYYYMMDD(d) };
    });
  }

  get selectedDate(): Date | null {
    return parseYYYYMMDD(this.value);
  }

  get calendarDate(): Date | null {
    return this.selectedDate;
  }

  set calendarDate(v: Date | null) {
    if (v) {
      const ymd = toYYYYMMDD(v);
      const displayDate = new Date(this.displayBase || todayStart());
      const maxSelectableDays = this.maxSelectableDays - 1;
      const cutoff = addDays(displayDate, maxSelectableDays);
      const vStart = new Date(v.getFullYear(), v.getMonth(), v.getDate());
      if (vStart > cutoff || vStart < displayDate) {
        const maxBase = addDays(this.maxCalendarDate, -maxSelectableDays);
        const newBase = vStart > maxBase ? maxBase : vStart;
        this.displayBase = toYYYYMMDD(newBase);
      }
      this.value = ymd;
      this.scrollToDate(v);
      (this.$refs.opCalendar as any)?.hide();
    }
  }

  get minCalendarDate(): Date {
    return todayStart();
  }

  get maxCalendarDate(): Date {
    // 15 hari termasuk hari ini → offset +14 dari today
    return addDays(todayStart(), CARGO_SEARCH_FLIGHT_MAX_SELECTABLE_DAYS - 1)
  }

  get calendarPage(): { month: number; year: number } {
    const d = this.selectedDate || todayStart();
    return { month: d.getMonth() + 1, year: d.getFullYear() };
  }

  isSelected(d: Date): boolean {
    const s = this.selectedDate;
    const e = parseYYYYMMDD(this.endValue);
    if (!s) return false;
    return (
      toYYYYMMDD(d) === toYYYYMMDD(s) ||
      (!!e && toYYYYMMDD(d) === toYYYYMMDD(e))
    );
  }

  formatButtonLabel(d: Date): string {
    const currentLocale = this.$i18n.locale === "my" ? "en" : this.$i18n.locale;
    return this.$moment(d)
      .locale(currentLocale)
      .format("dddd, DD MMM");
  }

  selectDate(d: Date): void {
    if (d > this.maxCalendarDate) return;
    this.value = toYYYYMMDD(d);
  }

  toggleCalendar(e: MouseEvent): void {
    (this.$refs?.opCalendar as any)?.toggle(e);
  }

  mounted(): void {
    this.picker = this.$refs.picker;
    this.overlayObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (nodeContainsOverlay(node)) {
            this.setOverlayVisible(true);
            return;
          }
        }
        for (const node of m.removedNodes) {
          if (nodeContainsOverlay(node)) {
            this.setOverlayVisible(false);
            return;
          }
        }
      }
    });
    this.overlayObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  beforeUnmount(): void {
    this.overlayObserver?.disconnect();
    this.overlayObserver = null;
  }

  scrollLeft() {
    const el = this.$refs.dateScroller as HTMLElement;
    el?.scrollBy({
      left: -250,
      behavior: "smooth",
    });
  }

  scrollRight() {
    const el = this.$refs.dateScroller as HTMLElement;
    el?.scrollBy({
      left: 250,
      behavior: "smooth",
    });
  }

  scrollToDate(date: Date) {
    this.$nextTick(() => {
      const container = this.$refs.dateScroller as HTMLElement;
      if (!container) return;

      const key = toYYYYMMDD(date);

      const button = container.querySelector(
        `[data-date="${key}"]`
      ) as HTMLElement | null;

      if (!button) return;

      button.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "center"
      });
    });
  }
}

export default SearchFlightDatePicker;
